/**
 * Firebase Configuration & Initialization
 *
 * Güvenlik notu:
 * - API key'ler environment variables'dan okunur (.env.local)
 * - .env.local dosyası .gitignore'da olduğundan git'e commit edilmez
 * - Client-side API key'ler public olabilir (Firebase Security Rules ile korunur)
 * - Asıl güvenlik: Firestore Security Rules + Cloud Functions
 */

import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import { getAuth, Auth, setPersistence, browserLocalPersistence, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, Firestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getFunctions, Functions, connectFunctionsEmulator } from 'firebase/functions';
import { getStorage, FirebaseStorage, connectStorageEmulator } from 'firebase/storage';
import { getAnalytics, Analytics } from 'firebase/analytics';
import { initializeAppCheck, ReCaptchaV3Provider, AppCheck } from 'firebase/app-check';

type AppCheckGlobal = typeof globalThis & {
  FIREBASE_APPCHECK_DEBUG_TOKEN?: string | boolean;
};

const appCheckGlobal = globalThis as AppCheckGlobal;

// Environment variables validation
const requiredEnvVars = [
  'REACT_APP_FIREBASE_API_KEY',
  'REACT_APP_FIREBASE_AUTH_DOMAIN',
  'REACT_APP_FIREBASE_PROJECT_ID',
  'REACT_APP_FIREBASE_STORAGE_BUCKET',
  'REACT_APP_FIREBASE_MESSAGING_SENDER_ID',
  'REACT_APP_FIREBASE_APP_ID',
  'REACT_APP_FIREBASE_MEASUREMENT_ID',
] as const;

// Validate environment variables
function validateEnvVars(): void {
  const missingVars = requiredEnvVars.filter(
    (varName) => !process.env[varName]
  );

  if (missingVars.length > 0) {
    throw new Error(
      `Missing required environment variables: ${missingVars.join(', ')}\n` +
      'Please check your .env.local file.'
    );
  }
}

// Firebase configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// Initialize Firebase (singleton pattern)
let app: FirebaseApp;
let auth: Auth;
let db: Firestore;
let storage: FirebaseStorage;
let analytics: Analytics;
let appCheck: AppCheck;
let functions: Functions;

// Enable App Check debug token support during local development
if (process.env.NODE_ENV === 'development') {
  const debugToken = process.env.REACT_APP_APPCHECK_DEBUG_TOKEN;

  if (debugToken && debugToken !== 'true') {
    appCheckGlobal.FIREBASE_APPCHECK_DEBUG_TOKEN = debugToken;
    console.log('✅ Firebase App Check debug token loaded from environment');
  } else if (!appCheckGlobal.FIREBASE_APPCHECK_DEBUG_TOKEN) {
    appCheckGlobal.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
    console.warn(
      '⚠️ Firebase App Check debug token auto mode enabled. Copy the generated token from console and set REACT_APP_APPCHECK_DEBUG_TOKEN.'
    );
  }
}

try {
  // Validate environment variables
  validateEnvVars();

  // Initialize Firebase only once
  if (!getApps().length) {
    app = initializeApp(firebaseConfig);
    console.log('✅ Firebase initialized successfully');
  } else {
    app = getApps()[0];
    console.log('✅ Firebase already initialized');
  }

  // Initialize services
  auth = getAuth(app);
  db = getFirestore(app);
  storage = getStorage(app);
  analytics = getAnalytics(app);
  functions = getFunctions(app, 'europe-west1');

  console.log('✅ Firebase Analytics initialized:', {
    measurementId: firebaseConfig.measurementId,
    analyticsInstance: Boolean(analytics),
  });

  // Connect to Emulators if running locally
  // Note: We use globalThis.location to avoid checking process.env.NODE_ENV which might be 'production' even locally during build tests
  if (globalThis.location?.hostname === 'localhost') {
    console.log('🔧 Connecting to Firebase Emulators...');
    // Auth
    connectAuthEmulator(auth, 'http://localhost:9088');
    // Firestore
    connectFirestoreEmulator(db, 'localhost', 8088);
    // Functions
    connectFunctionsEmulator(functions, 'localhost', 5088);
    // Storage
    connectStorageEmulator(storage, 'localhost', 9188);
  }

  // Initialize App Check (Bot Protection)
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  if (recaptchaSiteKey && recaptchaSiteKey !== 'YOUR_RECAPTCHA_SITE_KEY_HERE') {
    appCheck = initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(recaptchaSiteKey),
      isTokenAutoRefreshEnabled: true, // Token'ı otomatik yenile
    });
    console.log('✅ Firebase App Check initialized (Bot Protection Active)');
  } else {
    console.warn('⚠️ App Check not initialized: REACT_APP_RECAPTCHA_SITE_KEY missing');
  }

  // Set auth persistence to LOCAL (browser storage)
  setPersistence(auth, browserLocalPersistence)
    .then(() => {
      console.log('✅ Firebase Auth persistence set to LOCAL');
    })
    .catch((error) => {
      console.error('❌ Failed to set auth persistence:', error);
    });
} catch (error) {
  console.error('❌ Firebase initialization error:', error);
  throw error;
}

// Export Firebase services
export { app, auth, db, storage, analytics, appCheck, functions };

// Export Firebase types for convenience
export type { User } from 'firebase/auth';
export type { Timestamp, DocumentReference } from 'firebase/firestore';
